/**
 * Приглашение в комнату.
 *
 * Всплывает поверх любой страницы: зовут играть сейчас, и человек не должен
 * узнавать об этом, случайно заглянув в профиль. Приглашений может ждать
 * несколько — показывается свежее, остальные встанут следом.
 */
import { useTranslation } from "react-i18next";

import { useRespondToInvite, useRoomInvites } from "../api/queries";
import { play } from "../lib/sound";
import { useAuthStore } from "../stores/authStore";
import { Avatar } from "./Avatar";
import { Dialog } from "./Dialog";

export function InviteDialog() {
  const { t } = useTranslation();
  // Учётной записи, ждущей удаления, приглашения не показываем: играть ей
  // всё равно нельзя, а опрос сыпал бы отказами.
  const authenticated = useAuthStore(
    (state) => state.status === "authenticated" && !state.user?.deletionScheduledAt,
  );
  const invites = useRoomInvites(authenticated).data?.invites ?? [];
  const respond = useRespondToInvite();

  const invite = invites[0];
  if (!invite?.id) return null;

  const answer = (accept: boolean) =>
    respond.mutate(
      { inviteId: invite.id!, accept },
      { onSuccess: () => accept && play("roomOpen") },
    );

  return (
    // Закрытие крестиком равносильно отказу: приглашение, оставшееся висеть,
    // всплыло бы снова на следующей странице.
    <Dialog title={t("invite.title")} onClose={() => answer(false)}>
      <div className="flex items-center gap-3">
        <Avatar
          url={invite.fromUser?.avatarUrl}
          username={invite.fromUser?.username ?? ""}
          role={invite.fromUser?.role}
          className="h-11 w-11"
        />
        <p className="text-sm text-ink-200">
          {t("invite.text", { username: invite.fromUser?.username ?? "" })}
        </p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={respond.isPending}
          onClick={() => answer(true)}
        >
          {t("invite.accept")}
        </button>
        <button
          type="button"
          className="btn-ghost"
          disabled={respond.isPending}
          onClick={() => answer(false)}
        >
          {t("common.cancel")}
        </button>
      </div>

      {respond.isError && (
        <p className="mt-3 text-sm text-rust">
          {respond.error instanceof Error ? respond.error.message : t("invite.failed")}
        </p>
      )}
    </Dialog>
  );
}
